import { GPR_POTENTIAL_ENABLED } from "@/features/player-detail/utils/playerPageHeaderConstants";

/**
 * URL-friendly slugs for the four KPI breakdown routes wired off the player
 * page hero tiles. Centralized so the route file, the press handler in
 * `PlayerPageHeaderInnerContainer`, and any future call site agree on spelling.
 */
export const BREAKDOWN_METRIC_SLUGS = {
  gpr: "gpr",
  potential: "gpr-potential",
  playerStyleFit: "player-style-fit",
  teamStyleFit: "team-style-fit",
  transferValue: "gemini-player-valuation",
} as const;

/**
 *
 */
export type BreakdownMetric = keyof typeof BREAKDOWN_METRIC_SLUGS;
/**
 *
 */
export type BreakdownMetricSlug =
  (typeof BREAKDOWN_METRIC_SLUGS)[BreakdownMetric];

/**
 * Inverse lookup — resolves a route param slug back to the indicator type the
 * breakdown screen needs to render the right modal. Keyed by the literal slug
 * union so unknown strings are rejected at the type level instead of resolving
 * to `undefined` at runtime.
 */
export const SLUG_TO_BREAKDOWN_METRIC: Record<
  BreakdownMetricSlug,
  BreakdownMetric
> = {
  [BREAKDOWN_METRIC_SLUGS.gpr]: "gpr",
  [BREAKDOWN_METRIC_SLUGS.potential]: "potential",
  [BREAKDOWN_METRIC_SLUGS.playerStyleFit]: "playerStyleFit",
  [BREAKDOWN_METRIC_SLUGS.teamStyleFit]: "teamStyleFit",
  [BREAKDOWN_METRIC_SLUGS.transferValue]: "transferValue",
};

/**
 * Type guard for the breakdown metric slug union. Lets callers narrow a route
 * param string before indexing into the tightened SLUG_TO_BREAKDOWN_METRIC
 * record. `gpr-potential` is rejected while GPR_POTENTIAL_ENABLED is off
 * (SE-8777), so a stale deep link takes the unknown-slug path instead of
 * opening an empty breakdown.
 * @param slug - Raw slug captured from the route param, possibly unknown
 * @returns True when the slug is one of the known, enabled BreakdownMetricSlug values
 */
export const isBreakdownMetricSlug = (
  slug: string
): slug is BreakdownMetricSlug =>
  Object.prototype.hasOwnProperty.call(SLUG_TO_BREAKDOWN_METRIC, slug) &&
  (GPR_POTENTIAL_ENABLED || slug !== BREAKDOWN_METRIC_SLUGS.potential);
